"use client"

import Link from "next/link"
import Image from "next/image"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useEffect, useState } from "react"

export function FeaturedCategories({ hideHeading = false, hideViewAll = false }: { hideHeading?: boolean, hideViewAll?: boolean }) {
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch("/api/categories")
        if (!res.ok) throw new Error("Failed to fetch categories")
        const data = await res.json()
        setCategories(data)
      } catch (err: any) {
        setError(err.message || "Unknown error")
      } finally {
        setLoading(false)
      }
    }
    fetchCategories()
  }, [])

  if (loading) {
    return (
      <section className="py-16 lg:py-20 xl:py-24 bg-muted/30">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Shop by Category</h2>
          <p className="text-lg lg:text-xl xl:text-2xl text-muted-foreground mb-8 lg:mb-12">Loading categories...</p>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-16 lg:py-20 xl:py-24 bg-muted/30">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Shop by Category</h2>
          <p className="text-lg lg:text-xl xl:text-2xl text-red-500 mb-8 lg:mb-12">{error}</p>
        </div>
      </section>
    )
  }

  return (
    <section className="py-4 md:py-6 bg-muted">
      <div className="w-full max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
        {!hideHeading && (
          <div className="text-center mb-12 lg:mb-16 xl:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6">Shop by Category</h2>
            <p className="text-lg lg:text-xl xl:text-2xl text-muted-foreground max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
              Explore our wide range of products across different categories
            </p>
          </div>
        )}
        <div className="flex gap-2 md:gap-3 lg:gap-4 xl:gap-5 overflow-x-auto pb-2 scrollbar-hide justify-start">
          {categories.map((category) => (
            <Link key={category.slug} href={`/category/${category.slug}`} className="flex-shrink-0">
              <Card className="group hover:shadow-lg transition-all duration-slow hover:-translate-y-1 h-full w-48 md:w-56 lg:w-64 xl:w-72">
                <CardContent className="p-4 lg:p-6 xl:p-8 text-center h-full flex flex-col justify-center">
                  {/* Removed category image */}
                  <div className="flex-1 flex flex-col justify-center">
                    <h3 className="font-semibold mb-1 lg:mb-2 text-sm lg:text-base xl:text-lg">{category.name}</h3>
                    <p className="text-xs lg:text-sm xl:text-base text-muted-foreground">
                      {category._count?.products || 0} items
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        {!hideViewAll && (
          <div className="text-center mt-12 lg:mt-16 xl:mt-20">
            <Link href="/categories">
              <Button size="lg" variant="outline" className="text-lg px-8 py-3">
                View All Categories
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}
